import React, { useState, useEffect } from 'react';
import { Box, Typography, Fade, useTheme } from '@mui/material';

interface Activity {
  id: number;
  message: string;
}

const generateRandomActivity = (): Activity => {
  const messages = [
    '🎉 New photographer joined from New York',
    '🚀 Real estate agent signed up from Los Angeles', 
    '✨ Home service pro registered from Chicago',
    '💼 Law firm started trial from Houston',
    '⭐ Business upgraded to Pro from Phoenix',
    '📈 Client booked 47 appointments this week',
    '⏰ User saved 18 hours this month in Miami',
    '🎯 Response time improved 92% in Seattle',
    '🏆 Studio increased bookings 65% in Austin',
    '✅ Company enabled AI assistant in Denver',
    '🌟 Team added 5 users in Boston',
    '🔥 Agency purchased Enterprise from Portland',
  ];

  return {
    id: Date.now() + Math.random(),
    message: messages[Math.floor(Math.random() * messages.length)]
  };
};

const ActivityFeed: React.FC = () => {
  const theme = useTheme();
  const [currentActivity, setCurrentActivity] = useState<Activity | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Initial activity after 2 seconds
    const initialTimer = setTimeout(() => {
      setCurrentActivity(generateRandomActivity());
      setVisible(true);
    }, 2000);

    // Then rotate activities every 5-8 seconds
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentActivity(generateRandomActivity());
        setVisible(true);
      }, 500);
    }, 5000 + Math.random() * 3000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);

  if (!currentActivity) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 500,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Fade in={visible} timeout={500}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            px: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(146, 209, 81, 0.12)' 
              : 'rgba(146, 209, 81, 0.08)',
            backdropFilter: 'blur(10px)',
            borderTop: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.3s ease',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              fontWeight: 500,
              color: theme.palette.text.secondary,
              letterSpacing: '0.02em',
              animation: 'fadeSlide 0.5s ease',
              '@keyframes fadeSlide': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {currentActivity.message}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default ActivityFeed;